import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { QRCodeSVG } from 'qrcode.react';
import { jsPDF } from "jspdf";
import QRCode from 'qrcode';

const PrintLabels = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [printList, setPrintList] = useState([]); // [{ product, quantity }]

    // PDF Settings
    const [paperFormat, setPaperFormat] = useState('a4');
    const [orientation, setOrientation] = useState('portrait');
    const [columns, setColumns] = useState(3);
    const [qrSource, setQrSource] = useState('sku'); // 'sku', 'barcode', or 'id'

    useEffect(() => {
        const fetchProducts = async () => {
            if (!user?.companyId) return;
            try {
                const res = await fetch(`/api/products?companyId=${user.companyId}&limit=500&search=${search}&lite=true`);
                if (res.ok) {
                    const data = await res.json();
                    setProducts(data.products || []);
                }
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };

        const debounce = setTimeout(fetchProducts, 300);
        return () => clearTimeout(debounce);
    }, [user?.companyId, search]);

    const addToPrintList = (product) => {
        setPrintList(prev => {
            const existing = prev.find(item => item.product.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.product.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { product, quantity: 1 }];
        });
        showToast(`Ajouté à la liste d'impression`, "info");
    };

    const updateQuantity = (productId, qty) => {
        if (qty <= 0) {
            setPrintList(prev => prev.filter(item => item.product.id !== productId));
        } else {
            setPrintList(prev => prev.map(item =>
                item.product.id === productId ? { ...item, quantity: qty } : item
            ));
        }
    };

    const removeFromList = (productId) => {
        setPrintList(prev => prev.filter(item => item.product.id !== productId));
    };

    const handleGeneratePDF = async () => {
        if (printList.length === 0) {
            showToast("La liste d'impression est vide", "warning");
            return;
        }

        try {
            showToast("Génération du PDF en cours...", "info");

            const doc = new jsPDF({
                orientation: orientation,
                unit: 'mm',
                format: paperFormat
            });

            // Get page dimensions
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            const margin = 10;
            const usableWidth = pageWidth - (margin * 2);
            const usableHeight = pageHeight - (margin * 2);

            const labelWidth = usableWidth / columns;
            // Set label height proportional to width to fit square QR code and text
            const labelHeight = Math.min(labelWidth * 1.3, usableHeight / (Math.floor(usableHeight / (labelWidth * 1.3))));

            let currentX = margin;
            let currentY = margin;
            let currentColumn = 0;

            for (const item of printList) {
                for (let i = 0; i < item.quantity; i++) {
                    // Check if we need a new page
                    if (currentY + labelHeight > pageHeight - margin) {
                        doc.addPage();
                        currentX = margin;
                        currentY = margin;
                        currentColumn = 0;
                    }

                    // Determine QR content based on user selection
                    let qrContent = item.product.sku;
                    if (qrSource === 'barcode' && item.product.barcode) {
                        qrContent = item.product.barcode;
                    } else if (qrSource === 'externalReference' && item.product.externalReference) {
                        qrContent = item.product.externalReference;
                    }

                    // Generate QR code as data URI
                    const qrDataUrl = await QRCode.toDataURL(qrContent, {
                        margin: 1,
                        width: 200,
                        color: {
                            dark: '#000000',
                            light: '#ffffff'
                        }
                    });

                    // Draw Label Border
                    doc.setDrawColor(200);
                    doc.setLineWidth(0.2);
                    doc.rect(currentX, currentY, labelWidth, labelHeight);

                    // Draw QR Code
                    // Leave space for text at the bottom.
                    const textSpace = 15;
                    const maxQrSize = Math.min(labelWidth - 10, labelHeight - textSpace - 5);
                    const qrX = currentX + (labelWidth - maxQrSize) / 2;
                    const qrY = currentY + 5;

                    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, maxQrSize, maxQrSize);

                    // Draw Text
                    doc.setTextColor(0, 0, 0);

                    // Product Name (truncated)
                    doc.setFontSize(9);
                    doc.setFont("helvetica", "bold");
                    const titleText = doc.splitTextToSize(item.product.name, labelWidth - 4);
                    doc.text(titleText[0], currentX + labelWidth / 2, qrY + maxQrSize + 5, { align: 'center' });

                    // SKU
                    doc.setFontSize(8);
                    doc.setFont("helvetica", "normal");
                    doc.text(`SKU: ${item.product.sku}`, currentX + labelWidth / 2, qrY + maxQrSize + 9, { align: 'center' });

                    // EAN if exists
                    if (item.product.barcode) {
                        doc.setFontSize(7);
                        doc.setTextColor(100, 100, 100);
                        doc.text(`EAN: ${item.product.barcode}`, currentX + labelWidth / 2, qrY + maxQrSize + 13, { align: 'center' });
                    }

                    // Move to next cell
                    currentColumn++;
                    currentX += labelWidth;

                    if (currentColumn >= columns) {
                        currentColumn = 0;
                        currentX = margin;
                        currentY += labelHeight;
                    }
                }
            }

            // Save the PDF
            doc.save('etiquettes-inventaire.pdf');
            showToast("PDF généré avec succès ! Liste d'impression vidée.", "success");
            setPrintList([]); // Clear the list after valid generation

        } catch (error) {
            console.error("PDF generation error:", error);
            showToast("Erreur lors de la génération du PDF", "error");
        }
    };

    const totalLabels = printList.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <div className="flex flex-col h-[calc(100vh-150px)] lg:h-[calc(100vh-150px)] w-full">
            {/* Header Section */}
            <div className="print:hidden shrink-0 sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-black leading-tight tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Impression Étiquettes</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Générez des QR Codes professionnels pour vos articles ou cartons.</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleGeneratePDF}
                            disabled={totalLabels === 0}
                            className="flex items-center justify-center gap-2 rounded-lg h-10 px-6 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">picture_as_pdf</span>
                            Générer PDF {totalLabels > 0 && `(${totalLabels})`}
                        </button>
                    </div>
                </div>
            </div>

            <div className="print:hidden grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4 flex-1 min-h-0">
                {/* Left Column: Search & Select */}
                <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col gap-4 shadow-sm h-full min-h-0">
                    <h2 className="font-bold text-slate-800 dark:text-slate-200">Rechercher un produit</h2>
                    <div className="relative shrink-0">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                        <input
                            type="text"
                            placeholder="Rechercher par nom, SKU..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none transition-all"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-2 min-h-0">
                        {products.map(p => {
                            const listEntry = printList.find(item => item.product.id === p.id);
                            const isInList = !!listEntry;

                            return (
                                <div key={p.id} className={`flex justify-between items-center p-3 border rounded-lg transition-colors ${isInList ? 'border-primary/50 bg-primary/5 dark:bg-primary/10 shadow-sm shadow-primary/5' : 'border-slate-100 dark:border-slate-800 hover:border-primary/30 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                                    <div className="flex flex-col gap-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-sm text-slate-800 dark:text-slate-200">{p.name}</span>
                                            {isInList && (
                                                <span className="text-[10px] font-bold bg-primary text-white px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shadow-sm">
                                                    <span className="material-symbols-outlined text-[10px] font-bold">check</span>
                                                    Séléctionné ({listEntry.quantity})
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${qrSource === 'sku' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>SKU: {p.sku}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${qrSource === 'barcode' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>EAN: {p.barcode || 'N/A'}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${qrSource === 'externalReference' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>REF: {p.externalReference || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => addToPrintList(p)}
                                        className={`p-1.5 rounded-md transition-colors flex items-center justify-center shrink-0 ${isInList ? 'bg-primary text-white hover:bg-primary/90 shadow-sm shadow-primary/20' : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'}`}
                                        title={isInList ? "Ajouter un de plus" : "Ajouter à la liste"}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">add</span>
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Right Column: List & Settings */}
                <div className="flex flex-col gap-6 h-full min-h-0">
                    {/* Settings Panel */}
                    <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col gap-4 shadow-sm shrink-0">
                        <h2 className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                            <span className="material-symbols-outlined">settings</span>
                            Format d'impression
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Format de papier</label>
                                <select
                                    value={paperFormat}
                                    onChange={(e) => setPaperFormat(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="a4">A4</option>
                                    <option value="a5">A5</option>
                                    <option value="letter">Letter</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Orientation</label>
                                <select
                                    value={orientation}
                                    onChange={(e) => setOrientation(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="portrait">Portrait</option>
                                    <option value="landscape">Paysage</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Étiquettes par ligne</label>
                                <select
                                    value={columns}
                                    onChange={(e) => setColumns(Number(e.target.value))}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value={1}>1 (Très grand)</option>
                                    <option value={2}>2 colonnes</option>
                                    <option value={3}>3 colonnes</option>
                                    <option value={4}>4 colonnes</option>
                                    <option value={5}>5 colonnes (Petit)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Source du QR Code</label>
                                <select
                                    value={qrSource}
                                    onChange={(e) => setQrSource(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="sku">Référence / SKU</option>
                                    <option value="barcode">Code-barres / EAN</option>
                                    <option value="externalReference">Réf. Externe CRM</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Print List */}
                    <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col gap-4 shadow-sm flex-1 min-h-0">
                        <div className="flex justify-between items-center shrink-0">
                            <h2 className="font-bold text-slate-800 dark:text-slate-200">Liste d'impression</h2>
                            <span className="text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-3 py-1 rounded-full">
                                {totalLabels} étiquette(s)
                            </span>
                        </div>

                        {printList.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 min-h-0">
                                <span className="material-symbols-outlined text-4xl mb-2 opacity-50">format_list_bulleted</span>
                                <p className="text-sm">Aucun produit sélectionné</p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-2 min-h-0">
                                {printList.map(item => (
                                    <div key={item.product.id} className="flex gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/30 items-center">
                                        <div className="bg-white p-1 rounded border border-slate-200">
                                            <QRCodeSVG
                                                value={qrSource === 'barcode' && item.product.barcode ? item.product.barcode : (qrSource === 'externalReference' && item.product.externalReference ? item.product.externalReference : item.product.sku)}
                                                size={40}
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col overflow-hidden min-w-0 pr-2">
                                            <span className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">{item.product.name}</span>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono shrink-0 ${qrSource === 'sku' ? 'bg-primary/10 text-primary font-bold' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>SKU: {item.product.sku}</span>
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono shrink-0 ${qrSource === 'barcode' ? 'bg-primary/10 text-primary font-bold' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>EAN: {item.product.barcode || 'N/A'}</span>
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono shrink-0 hidden sm:inline-flex ${qrSource === 'externalReference' ? 'bg-primary/10 text-primary font-bold' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>REF: {item.product.externalReference || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="0"
                                                value={item.quantity}
                                                onChange={(e) => updateQuantity(item.product.id, parseInt(e.target.value) || 0)}
                                                className="w-16 text-center py-1.5 px-2 text-sm font-bold bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-primary outline-none"
                                            />
                                            <button
                                                onClick={() => removeFromList(item.product.id)}
                                                className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintLabels;
